import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/data-table/DataTable';
import { formatDA, formatDateDZ } from '@/lib/format';

interface DemoClient {
  id: string;
  raison_sociale: string;
  ville: string;
  encours: number;
  derniere_facture: string;
  statut: 'a_jour' | 'retard' | 'litige';
}

const DATA: DemoClient[] = [
  {
    id: 'C-001',
    raison_sociale: 'SARL Algéria Telecom Services',
    ville: 'Alger',
    encours: 2_450_000,
    derniere_facture: '2026-04-12',
    statut: 'a_jour',
  },
  {
    id: 'C-002',
    raison_sociale: 'EURL Saidal Pharma',
    ville: 'Constantine',
    encours: 1_280_500,
    derniere_facture: '2026-03-28',
    statut: 'retard',
  },
  {
    id: 'C-003',
    raison_sociale: 'SPA Cevital Industrie',
    ville: 'Béjaïa',
    encours: 8_900_000,
    derniere_facture: '2026-04-22',
    statut: 'a_jour',
  },
  {
    id: 'C-004',
    raison_sociale: 'EURL Naftal Distribution',
    ville: 'Oran',
    encours: 3_600_000,
    derniere_facture: '2026-02-15',
    statut: 'litige',
  },
  {
    id: 'C-005',
    raison_sociale: 'SARL Hydra Technologies',
    ville: 'Alger',
    encours: 540_000,
    derniere_facture: '2026-04-30',
    statut: 'a_jour',
  },
  {
    id: 'C-006',
    raison_sociale: 'SARL Kabyle Construction',
    ville: 'Tizi Ouzou',
    encours: 1_950_000,
    derniere_facture: '2026-03-08',
    statut: 'retard',
  },
  {
    id: 'C-007',
    raison_sociale: 'EURL Sahel Logistics',
    ville: 'Annaba',
    encours: 720_000,
    derniere_facture: '2026-04-18',
    statut: 'a_jour',
  },
  {
    id: 'C-008',
    raison_sociale: 'SPA Sonatrach Engineering',
    ville: 'Hassi Messaoud',
    encours: 12_300_000,
    derniere_facture: '2026-04-25',
    statut: 'a_jour',
  },
  {
    id: 'C-009',
    raison_sociale: 'SARL Atlas Médical',
    ville: 'Sétif',
    encours: 880_000,
    derniere_facture: '2026-03-19',
    statut: 'retard',
  },
  {
    id: 'C-010',
    raison_sociale: 'EURL Mitidja Agro',
    ville: 'Blida',
    encours: 1_120_000,
    derniere_facture: '2026-04-05',
    statut: 'a_jour',
  },
  {
    id: 'C-011',
    raison_sociale: 'SARL Orient Distribution',
    ville: 'Batna',
    encours: 410_000,
    derniere_facture: '2026-04-10',
    statut: 'a_jour',
  },
  {
    id: 'C-012',
    raison_sociale: 'SPA Condor Electronics',
    ville: 'Bordj Bou Arreridj',
    encours: 6_750_000,
    derniere_facture: '2026-04-02',
    statut: 'litige',
  },
];

const STATUT_LABELS: Record<
  DemoClient['statut'],
  { label: string; variant: 'success' | 'warning' | 'destructive' }
> = {
  a_jour: { label: 'À jour', variant: 'success' },
  retard: { label: 'En retard', variant: 'warning' },
  litige: { label: 'Litige', variant: 'destructive' },
};

export function TestDataTablePage(): JSX.Element {
  const columns = useMemo<ColumnDef<DemoClient>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Réf',
        size: 90,
        cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span>,
      },
      { accessorKey: 'raison_sociale', header: 'Raison sociale' },
      { accessorKey: 'ville', header: 'Ville', size: 140 },
      {
        accessorKey: 'encours',
        header: 'Encours',
        size: 140,
        cell: ({ getValue }) => <span className="num">{formatDA(Number(getValue()))}</span>,
      },
      {
        accessorKey: 'derniere_facture',
        header: 'Dernière facture',
        size: 140,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatDateDZ(String(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'statut',
        header: 'Statut',
        size: 120,
        cell: ({ getValue }) => {
          const meta = STATUT_LABELS[getValue() as DemoClient['statut']];
          return <Badge variant={meta.variant}>{meta.label}</Badge>;
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold">Test DataTable</h1>
        <p className="text-sm text-muted-foreground">
          12 clients fictifs — démontre tri, recherche, sélection multiple, densité, pagination,
          export CSV.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Clients (démo)</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={DATA}
            columns={columns}
            enableSelection
            pageSize={10}
            exportFileName="clients-demo.csv"
            searchPlaceholder="Rechercher un client…"
          />
        </CardContent>
      </Card>
    </div>
  );
}
